import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Filter } from 'lucide-react';
import { api } from '@/utils/api';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import DealCard from '@/components/broker/DealCard';
import { DashboardSkeleton, ErrorBanner } from '@/components/ui/loading-states';

export default function BrokerMarketplace() {
  const [activeTab, setActiveTab] = useState('open'); // 'open' or 'funded'
  const [filterCity, setFilterCity] = useState('All');
  const [openDeals, setOpenDeals] = useState([]);
  const [fundedDeals, setFundedDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    setError(null);
    try {
      // Use Phase 3 unified marketplace endpoint
      const response = await api.get('/broker/marketplace');
      const deals = response.data.deals || [];
      
      const open = deals.filter(d => d.openUnits > 0).map(d => ({
        id: d.projectId,
        name: d.name,
        location: d.location?.sector || d.location?.fullAddress || '',
        city: d.location?.city || '',
        assetValue: d.unitValue || 0,
        capitalRequired: d.unitValue ? d.unitValue * 0.3 : 0,
        capitalRaised: 0,
        fundingPercent: 0,
        minTicket: d.minInvestment || 500000,
        daysLeft: 30,
        totalSlots: d.totalUnits || 0,
        filledSlots: d.fundedUnits || 0,
        status: 'live',
        unitCount: d.openUnits || 0,
      }));
      
      const funded = deals.filter(d => d.fundedUnits > 0 && d.openUnits === 0).map(d => ({
        id: d.projectId,
        name: d.name,
        location: d.location?.sector || d.location?.fullAddress || '',
        city: d.location?.city || '',
        assetValue: d.unitValue || 0,
        capitalRequired: d.unitValue ? d.unitValue * 0.3 : 1,
        capitalRaised: d.unitValue ? d.unitValue * 0.3 : 0,
        fundingPercent: 100,
        status: 'funded',
        unitCount: d.fundedUnits || 0,
      }));
      
      setOpenDeals(open);
      setFundedDeals(funded);
    } catch (err) {
      console.error('Error fetching deals:', err);
      setError('Failed to load marketplace deals.');
      setOpenDeals([]);
      setFundedDeals([]);
    } finally {
      setLoading(false);
    }
  };

  const deals = activeTab === 'open' ? openDeals : fundedDeals;
  const cities = ['All', ...new Set(deals.map(d => d.city).filter(Boolean))];
  const filteredDeals = filterCity === 'All' ? deals : deals.filter(d => d.city === filterCity);

  return (
    <div className="min-h-screen bg-black flex">
      <BrokerSidebar />
      <main className="flex-1 ml-64 p-10">
        {error && <ErrorBanner message={error} onRetry={fetchDeals} />}
        {loading ? <DashboardSkeleton cards={3} rows={4} /> : (<>
        <div className="mb-8">
          <h1 className="text-2xl font-light text-white mb-2">Deal Marketplace</h1>
          <p className="text-sm text-gray-500">Browse investment opportunities</p>
        </div>

        {/* Tabs for Open vs Funded */}
        <div className="mb-6 flex gap-4 border-b border-white/10">
          <button
            onClick={() => { setActiveTab('open'); setFilterCity('All'); }}
            className={`pb-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'open' 
                ? 'text-emerald-400 border-b-2 border-emerald-400' 
                : 'text-gray-500 hover:text-white'
            }`}
            data-testid="tab-open-opportunities"
          >
            Open Opportunities ({openDeals.length})
          </button>
          <button
            onClick={() => { setActiveTab('funded'); setFilterCity('All'); }}
            className={`pb-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'funded' 
                ? 'text-emerald-400 border-b-2 border-emerald-400' 
                : 'text-gray-500 hover:text-white'
            }`}
            data-testid="tab-funded-opportunities"
          >
            Funded Opportunities ({fundedDeals.length})
          </button>
        </div>

        <div className="mb-6 flex gap-4">
          <select 
            value={filterCity} 
            onChange={(e) => setFilterCity(e.target.value)} 
            className="bg-white/5 border border-white/10 text-white px-4 py-2 text-sm"
          >
            {cities.map(city => <option key={city} value={city}>{city}</option>)}
          </select>
        </div>

        {loading ? (
          <p className="text-gray-500">Loading deals...</p>
        ) : filteredDeals.length === 0 ? (
          <div className="border border-white/10 p-12 text-center">
            <p className="text-gray-500">
              {activeTab === 'open' ? 'No open opportunities available' : 'No funded opportunities yet'}
            </p>
            <p className="text-gray-700 text-sm mt-2">
              {activeTab === 'open' 
                ? 'Properties will appear when added by admin' 
                : 'Funded deals will appear here after tranche completion'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {filteredDeals.map((deal) => (
              <DealCard key={deal.id} deal={deal} brokerCumulativeCapital={4500000} />
            ))}
          </div>
        )}
        </>)}
      </main>
    </div>
  );
}
